import { Tags } from 'lucide-react'
import { defineModule } from '@/shell/module-system'
import { CustomerPricelistsPage } from './pages/CustomerPricelistsPage'
import { PriceChangeLetterPage } from './pages/PriceChangeLetterPage'
import { PriceChangeLettersPage } from './pages/PriceChangeLettersPage'
import { PriceReviewPage } from './pages/PriceReviewPage'
import { PriceReviewsPage } from './pages/PriceReviewsPage'
import { useReviewsNavBadge } from './nav-badge'

/**
 * Customer Pricelists — Sprint 01 (Phase 2 opener). MasterDetail
 * archetype: customer pricelist list on the left, EditableGrid of
 * pricelist items on the right. Gated to admin + sales because pricing
 * is Sales-sensitive.
 *
 * Middleware endpoints land under `/api/pricelists/*` in this sprint
 * — see docs/active/sprint-01-customer-pricelists.md. No badge, no
 * optimistic-concurrency contract: Odoo is the source of truth
 * (Sprint 01 Option A), a toolbar Refresh handles external edits.
 */
export default defineModule({
  id: 'customer-pricelists',
  name: 'Customer Pricelists',
  shortName: 'Pricelists',
  icon: Tags,
  navSection: 'sales',
  navOrder: 40,
  urlNamespace: '/pricelists',
  routes: [
    { path: '/', element: CustomerPricelistsPage, label: 'Customer Pricelists' },
    { path: '/letters', element: PriceChangeLettersPage, label: 'Price change letters' },
    { path: '/letters/$letterId', element: PriceChangeLetterPage, label: 'Price change letter' },
    { path: '/reviews', element: PriceReviewsPage, label: 'Price reviews' },
    { path: '/reviews/$reviewId', element: PriceReviewPage, label: 'Price review' },
  ],
  permissions: ['admin', 'sales'],
  densityDefault: 'comfortable',
  filterRailDefault: 'collapsed',
  mobileStrategy: 'desktop-only',

  // Admin-only count of price reviews waiting for approval.
  useNavBadge: useReviewsNavBadge,

  // Sprint 01 spec requested `g p` but that is already bound by the M9
  // Procurement module. Override documented in the sprint md's
  // "During-sprint decisions". `g l` falls out of "priceList" and is
  // unique across the 12 registered modules.
  keyboardShortcut: 'g l',

  commandPaletteEntries: [
    {
      label: 'Go to Customer Pricelists',
      keywords: [
        'pricelist',
        'pricelists',
        'customer',
        'pricing',
        'part number',
      ],
      path: '/',
      group: 'modules',
      shortcut: 'g l',
    },
    {
      label: 'Go to Price reviews',
      keywords: ['price review', 'review', 'pricing update', 'approve', 'new prices', 'margin'],
      path: '/reviews',
      group: 'modules',
    },
    {
      label: 'Go to Price change letters',
      keywords: ['price change', 'letter', 'letters', 'price increase', 'pricing update'],
      path: '/letters',
      group: 'modules',
    },
  ],

  deepLinkHandlers: [
    {
      entity: 'customer',
      label: 'View customer pricelist',
      buildPath: (customerId) => `/?partner=${customerId}`,
    },
    {
      entity: 'product',
      label: 'Find pricelists with this product',
      buildPath: (productId) => `/?product=${productId}`,
    },
  ],

  consumesServices: ['pricing-engine', 'odoo-search'],
})
